import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Cpu, Home, Pause, Play, Wifi } from "lucide-react";
import { OnlineUsersDialog } from "../components/OnlineUsersDialog";
import { api, errMsg } from "../lib/api";
import { useRouters } from "../context/RouterContext";
import { Button } from "../components/ui/button";

const MAX_POINTS = 90;
const fmtBps = (b) => (b >= 1e9 ? `${(b / 1e9).toFixed(2)} Gbps` : b >= 1e6 ? `${(b / 1e6).toFixed(1)} Mbps` : b >= 1e3 ? `${(b / 1e3).toFixed(0)} kbps` : `${b} bps`);
const fmtBytes = (b) => (b >= 2 ** 30 ? `${(b / 2 ** 30).toFixed(1)} GiB` : b >= 2 ** 20 ? `${(b / 2 ** 20).toFixed(1)} MiB` : b >= 1024 ? `${(b / 1024).toFixed(0)} KiB` : `${b} B`);
const fmtPps = (n) => `${Number(n || 0).toLocaleString("id-ID").replace(/\./g, " ")} p/s`;
const niceMax = (v) => { if (v <= 0) return 1; const p = 10 ** Math.floor(Math.log10(v)); const m = v / p; const n = m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10; return n * p; };

function BarChart({ series, fmt, testid }) {
  const ref = useRef(null);
  const peak = Math.max(1, ...series.map((s) => Math.max(s.a, s.b)));
  const top = niceMax(peak * 1.1);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const W = c.clientWidth, H = 240;
    c.width = W * dpr; c.height = H * dpr;
    const ctx = c.getContext("2d"); ctx.scale(dpr, dpr);
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "#cfd4dc"; ctx.lineWidth = 1;
    for (let x = 0; x <= W; x += W / 40) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y <= H; y += H / 12) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
    const bw = W / MAX_POINTS;
    series.forEach((s, i) => {
      const x = W - (series.length - i) * bw;
      const hb = (s.b / top) * H, ha = (s.a / top) * H;
      ctx.fillStyle = "#e11d1d"; ctx.fillRect(x + 1, H - hb, Math.max(1, bw - 2), hb);
      ctx.fillStyle = "#1d1de1"; ctx.fillRect(x + 1, H - ha, Math.max(1, bw - 2), ha);
    });
    ctx.strokeStyle = "#555"; ctx.strokeRect(0.5, 0.5, W - 1, H - 1);
  }, [series, top]);
  return (
    <div className="relative" data-testid={testid}>
      <canvas ref={ref} className="w-full h-[240px] block" />
      <div className="absolute left-3 bottom-3 bg-white/95 border border-slate-700 text-slate-900 text-sm px-3 py-2 space-y-1 shadow" data-testid={`${testid}-legend`}>
        {[["#1d1de1", "Upload", series.at(-1)?.a], ["#e11d1d", "Download", series.at(-1)?.b]].map(([c, l, v]) => (
          <div key={l} className="flex items-center gap-2"><span className="inline-block w-5 h-5 border border-slate-800" style={{ background: c }} />{l}: <b>{fmt(v || 0)}</b></div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5">
      <span className="text-sm text-slate-700">{label}</span>
      <span className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-mono min-w-[11rem] text-left" data-testid={testid}>{value}</span>
    </div>
  );
}

function StatGroup({ title, children }) {
  return <div className="rounded-lg border border-slate-200 bg-white overflow-hidden"><div className="bg-slate-50 border-b border-slate-200 px-4 py-2.5 font-semibold text-slate-800">{title}</div>{children}</div>;
}

export default function TrafficMonitor() {
  const { selected, router } = useRouters();
  const [ifaces, setIfaces] = useState([]);
  const [iface, setIface] = useState("");
  const [cur, setCur] = useState(null);
  const [rate, setRate] = useState([]);
  const [pkt, setPkt] = useState([]);
  const [err, setErr] = useState("");
  const [paused, setPaused] = useState(false);
  const [online_, setOnline_] = useState(null);
  const [onlineKind, setOnlineKind] = useState(null);
  useEffect(() => {
    if (!selected) return;
    const f = () => api.get(`/traffic/${selected}/online`).then((r) => setOnline_(r.data)).catch(() => {});
    f();
    const t = setInterval(f, 10000);
    return () => clearInterval(t);
  }, [selected]);
  useEffect(() => {
    if (!selected) return;
    setIfaces([]); setIface(""); setCur(null); setRate([]); setPkt([]); setErr("");
    api.get(`/traffic/${selected}/interfaces`).then((r) => { setIfaces(r.data); const first = r.data.find((i) => /^ether1/i.test(i.name)) || r.data.find((i) => i.running) || r.data[0]; setIface(first?.name || ""); })
      .catch((e) => setErr(errMsg(e)));
  }, [selected]);
  useEffect(() => { setRate([]); setPkt([]); setCur(null); }, [iface]);
  useEffect(() => {
    if (!selected || !iface || paused) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/traffic/${selected}/monitor`, { params: { iface } });
        if (!alive) return;
        setCur(data); setErr("");
        setRate((s) => [...s, { a: data.tx_bps, b: data.rx_bps }].slice(-MAX_POINTS));
        setPkt((s) => [...s, { a: data.tx_pps, b: data.rx_pps }].slice(-MAX_POINTS));
      } catch (e) { if (alive) setErr(errMsg(e)); }
    };
    tick();
    const t = setInterval(tick, 2000);
    return () => { alive = false; clearInterval(t); };
  }, [selected, iface, paused]);
  const online = cur && !err;
  return (
    <div className="min-h-screen bg-[#f4f5f7] text-slate-900 p-4 sm:p-8" data-testid="traffic-page">
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-1" data-testid="traffic-back"><ArrowLeft className="w-3 h-3" /> Dashboard</Link>
            <div className="text-xs tracking-[0.25em] font-semibold text-slate-500">FASNET FIBER</div>
            <h1 className="text-3xl sm:text-4xl font-bold">Monitor Trafik Interface</h1>
            <p className="text-slate-500 text-sm sm:text-base">Pantau trafik jaringan, paket data, dan status koneksi antarmuka router <b className="text-slate-800">{router?.name || ""}</b>.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setOnlineKind("hotspot")} className="rounded-lg border border-sky-300 bg-sky-50 text-sky-700 px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 hover:bg-sky-100 transition-colors" title="Klik untuk lihat daftar user" data-testid="traffic-hotspot-online">
              <Wifi className="w-4 h-4" /> Hotspot Online: <span className="font-mono text-base">{online_ ? online_.hotspot_online : "…"}</span>
            </button>
            <button type="button" onClick={() => setOnlineKind("ppp")} className="rounded-lg border border-violet-300 bg-violet-50 text-violet-700 px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 hover:bg-violet-100 transition-colors" title="Klik untuk lihat daftar user" data-testid="traffic-ppp-online">
              <Home className="w-4 h-4" /> Rumahan Online: <span className="font-mono text-base">{online_ ? online_.ppp_online : "…"}</span>
            </button>
            <OnlineUsersDialog routerId={selected} kind={onlineKind} onClose={() => setOnlineKind(null)} />
            <div className="rounded-lg border border-slate-300 bg-white text-slate-700 px-4 py-2 text-sm inline-flex flex-wrap items-center gap-x-4 gap-y-1" title={online_?.version ? `RouterOS ${online_.version} · uptime ${online_.uptime}` : ""} data-testid="traffic-board-info">
              <span className="inline-flex items-center gap-1.5"><Cpu className="w-4 h-4 text-slate-500" /> Mikrotik = <b className="font-mono">{online_ ? (online_.board || "-") : "…"}</b></span>
              <span>CPU = <b className={`font-mono ${online_?.cpu >= 80 ? "text-rose-600" : online_?.cpu >= 60 ? "text-amber-600" : ""}`}>{online_?.cpu ?? "…"}%</b></span>
              <span>Memory = <b className={`font-mono ${online_?.mem >= 85 ? "text-rose-600" : ""}`}>{online_?.mem ?? "…"}%</b></span>
              <span>Suhu = <b className={`font-mono ${online_?.temp >= 70 ? "text-rose-600" : ""}`}>{online_ ? (online_.temp != null ? `${online_.temp}°C` : "N/A") : "…"}</b></span>
            </div>
            <Button variant="outline" size="sm" onClick={() => setPaused((p) => !p)} data-testid="traffic-pause">{paused ? <><Play className="w-4 h-4 mr-1" /> Lanjut</> : <><Pause className="w-4 h-4 mr-1" /> Jeda</>}</Button>
            <div className={`rounded-lg border px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 ${online ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-rose-300 bg-rose-50 text-rose-700"}`} data-testid="traffic-status">
              <span className={`w-2.5 h-2.5 rounded-full ${online ? "bg-emerald-600" : "bg-rose-600"}`} /> Status Koneksi: {online ? "Aktif" : err ? "Gagal" : "Menghubungkan…"}
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center gap-4 px-5 py-4 border-b border-slate-200">
            <span className="text-lg font-semibold">Interface</span>
            <select value={iface} onChange={(e) => setIface(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white px-3 text-lg font-semibold min-w-[12rem]" data-testid="traffic-iface">
              {ifaces.map((i) => <option key={i.name} value={i.name}>{i.name}{i.comment ? ` — ${i.comment}` : ""}{i.running ? "" : " (down)"}</option>)}
            </select>
            {cur && <span className={`text-xs px-2 py-0.5 rounded ${cur.running ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`} data-testid="traffic-iface-running">{cur.running ? "running" : "link down"}</span>}
            {err && <span className="text-xs text-rose-600" data-testid="traffic-error">{err}</span>}
          </div>
          <div className="grid lg:grid-cols-[320px_1fr] gap-4 p-4">
            <div className="space-y-4">
              <StatGroup title="Kecepatan Trafik"><Stat label="Upload (TX):" value={fmtBps(cur?.tx_bps || 0)} testid="stat-tx" /><Stat label="Download (RX):" value={fmtBps(cur?.rx_bps || 0)} testid="stat-rx" /></StatGroup>
              <StatGroup title="Paket per Detik"><Stat label="Upload Packet:" value={fmtPps(cur?.tx_pps)} testid="stat-tx-pps" /><Stat label="Download Packet:" value={fmtPps(cur?.rx_pps)} testid="stat-rx-pps" /></StatGroup>
              <StatGroup title="Total Data"><Stat label="Upload Bytes:" value={fmtBytes(cur?.tx_bytes || 0)} testid="stat-tx-bytes" /><Stat label="Download Bytes:" value={fmtBytes(cur?.rx_bytes || 0)} testid="stat-rx-bytes" /></StatGroup>
              <StatGroup title="Kondisi Paket"><Stat label="Drop Packet:" value={cur?.drops ?? 0} testid="stat-drops" /><Stat label="Error:" value={cur?.errors ?? 0} testid="stat-errors" /></StatGroup>
            </div>
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 px-4 py-2.5 flex justify-between"><span className="font-semibold">Grafik Trafik Jaringan (Kecepatan)</span><span className="text-sm text-slate-500" data-testid="rate-scale">Skala atas: {fmtBps(niceMax(Math.max(1, ...rate.map((s) => Math.max(s.a, s.b))) * 1.1))}</span></div>
                <div className="p-3"><BarChart series={rate} fmt={fmtBps} testid="chart-rate" /></div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 px-4 py-2.5 flex justify-between"><span className="font-semibold">Grafik Paket Data (Paket per Detik)</span><span className="text-sm text-slate-500" data-testid="pkt-scale">Skala atas: {fmtPps(niceMax(Math.max(1, ...pkt.map((s) => Math.max(s.a, s.b))) * 1.1))}</span></div>
                <div className="p-3"><BarChart series={pkt} fmt={fmtPps} testid="chart-pkt" /></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
